import { useEffect } from 'react';
import { isInteractiveElement } from '@bynder-studio/misc';
import { ImageManipulationRenderer, PlaybackManipulationRenderer } from '@bynder-studio/render-web';
import { is } from 'rambda';
import { isCtrlKey } from 'packages/helpers/hotKeys';
import useArrangeActions from '../useArrangeActions';
import { useIsModalOpen } from '~/hooks/useIsModalOpen';

type Props = {
    manipulationRenderer: PlaybackManipulationRenderer | ImageManipulationRenderer;
};

export const useArrangeShortcuts = ({ manipulationRenderer }: Props) => {
    const { swapUpDown, moveUpDown, handleBringToFront, handleSendToBack } = useArrangeActions({
        manipulationRenderer,
    });
    const isModalOpen = useIsModalOpen();

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent): void => {
            if (isInteractiveElement(e) || isModalOpen) {
                return;
            }

            const isImageDesign = manipulationRenderer instanceof ImageManipulationRenderer;

            if (isCtrlKey(e)) {
                switch (e.code) {
                    case 'BracketLeft': {
                        e.preventDefault();

                        if (isImageDesign) {
                            swapUpDown('up');
                        } else {
                            moveUpDown('up');
                        }

                        break;
                    }

                    case 'BracketRight': {
                        e.preventDefault();

                        if (isImageDesign) {
                            swapUpDown('down');
                        } else {
                            moveUpDown('down');
                        }

                        break;
                    }

                    default:
                        break;
                }
            }

            if (!isCtrlKey(e)) {
                switch (e.code) {
                    case 'BracketLeft': {
                        e.preventDefault();
                        handleBringToFront();
                        break;
                    }

                    case 'BracketRight': {
                        e.preventDefault();
                        handleSendToBack();
                        break;
                    }

                    default:
                        break;
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [manipulationRenderer, isModalOpen]);
};
